// What the allotment looks like at each stage of growth.
//
// The sim owns the beds and their clocks (sim-server/src/world/Garden.js:
// seed, sprout, growing, ripe, on real hours, lost if he does not come).
// This is the other half: given a bed, what the viewer draws. Pure, and
// out here rather than in the component so it can be tested without a
// renderer, same as lib/world/mood.js and the rest of this folder.

// How big a plant stands at each stage, and whether the fruited models
// are among them. `seed` is bare soil: the sim has sown it and there is
// nothing to see yet, which is true of a seed.
export const STAGES = {
  seed: { scale: 0, fruit: false },
  sprout: { scale: 0.32, fruit: false },
  growing: { scale: 0.75, fruit: false },
  ripe: { scale: 1, fruit: true },
}

/**
 * What one plant should do, given the bed it stands in.
 *
 * `plant` carries where it was authored: `originY` is its node's height
 * and `baseOffset` is how far below that it meets the soil (negative in
 * this export, because the origins sit mid-plant). `fruited` marks the
 * models that carry fruit, which only belong in a ripe bed.
 *
 * `bed` is undefined in two very different situations and they must not
 * look the same:
 *
 *   • the sim is there and says the bed is empty  -> nothing grows
 *   • there is no sim at all (the director rig, a dropped socket, a page
 *     opened before the first world delta) -> draw the allotment as it
 *     was authored, full grown. "I do not know" rendering as bare earth
 *     meant the greenhouse could never be filmed, and a viewer that
 *     loses its socket should not quietly harvest his garden.
 */
export function plantLook(plant, bed, { known = true, sprouts = false, authored = null } = {}) {
  if (!known) {
    // As authored: Sam's tomatoes, peas and beans where he put them, and
    // one crop in the bed he left empty, never every crop at once
    if (plant.sprout) return { show: false, scale: 1, y: plant.originY }
    if (plant.crop && authored && !authored.includes(plant.crop)) return { show: false, scale: 1, y: plant.originY }
    return { show: true, scale: 1, y: plant.originY }
  }
  const stage = STAGES[bed?.stage] || STAGES.seed
  // A seedling is its own model, at its own size, and only while sprouting
  if (plant.sprout) {
    const show = !!bed?.crop && bed?.stage === 'sprout'
    return { show, scale: 1, y: plant.originY }
  }
  // Every bed carries every crop; only the one that was sown grows
  if (!cropMatches(plant.crop, bed?.crop)) return { show: false, scale: stage.scale, y: plant.originY }
  // With seedlings in the bed, the grown plant waits for the growing stage
  if (sprouts && bed?.stage === 'sprout') return { show: false, scale: stage.scale, y: plant.originY }
  const show = !!bed?.crop && stage.scale > 0 && (!plant.fruited || stage.fruit)
  if (!show) return { show: false, scale: stage.scale, y: plant.originY }
  return {
    show: true,
    scale: stage.scale,
    // Keep its feet in the soil. Scaling about the node's own origin
    // would lift the base by the part of the plant that sat below it, so
    // a seedling would hang in the air with its roots showing.
    y: plant.originY + plant.baseOffset * (1 - stage.scale),
  }
}

/**
 * The crop a plant belongs to, from its node name: fruit_<crop>_<n>. The
 * export names the crops the way the sim does, short: 'beans' for the
 * sim's 'runner beans'. Null for a name that says nothing, which matches
 * any crop (the old export, before every bed carried every crop).
 */
export function cropOf(name) {
  const m = /^fruit_([a-z]+)_/i.exec(String(name || ''))
  return m ? m[1].toLowerCase() : null
}

/** Whether a plant of this crop grows in a bed sown with that one. */
export function cropMatches(plantCrop, bedCrop) {
  if (!plantCrop) return true
  if (!bedCrop) return false
  const words = String(bedCrop).toLowerCase().split(/\s+/)
  return words.includes(plantCrop) || String(bedCrop).toLowerCase() === plantCrop
}

// What each bed shows with no sim to say (the director rig, a dropped
// socket): what Sam planted, and strawberries in the third bed, which he
// left empty. Bed index, as the export's bed_<n> minus one.
export const AUTHORED_CROPS = [['tomatoes'], ['peas', 'beans'], ['strawberries']]
